import Decimal from 'decimal.js';
import { cloneDeep } from 'lodash-es';
import { mathFormat } from '@/pages/home/utils/search';
import { MarketBarProps } from '.';
import { toFixed0, TOTAL_BAR_SERIES_NAME } from '../../MarketSize/utils';

const recalcTotalSerie = (series?: any[], legendSelected?: Record<string, boolean>): any[] => {
  if (!Array.isArray(series) || series.length === 0) return [];
  if (!legendSelected) return series;
  const serieNames = series.map((serie) => serie?.name).filter((name) => !['YoY', TOTAL_BAR_SERIES_NAME].includes(name));
  const isNeedRecalcTotal = serieNames.some((name) => legendSelected[name] === false);
  if (!isNeedRecalcTotal) return series;
  const seriesTmp = cloneDeep(series);
  const totalSerieTmp = seriesTmp.find((serie) => serie?.type === 'bar' && serie?.name === TOTAL_BAR_SERIES_NAME && !serie?.stack);
  if (!Array.isArray(totalSerieTmp?.data)) return seriesTmp;

  totalSerieTmp.data = totalSerieTmp.data.map((valueObj: any, index: number) => {
    const totalValueTmp = serieNames.reduce((acc, cur) => {
      if (!cur) return acc;
      if (!legendSelected[cur]) return acc;
      const serieData = seriesTmp.find((item) => item?.name === cur);
      return Decimal(acc).add(Decimal(serieData?.data?.[index]?.value)).toString();
    }, 0);

    return { ...valueObj, value: String(totalValueTmp) };
  });

  return seriesTmp;
};

export const marketConfig = (props: Partial<MarketBarProps>) => {
  // 后续覆盖即可
  const {
    series,
    legend,
    xAxis,
    legendSelected,
    legendTooltipFormatter,
    tooltipFormatter,
    splitNumber,
    locale,
    tooltipTitle,
    ...resetProps
  } = props || {};
  return {
    grid: {
      left: 2,
      right: 5,
      bottom: 24,
      top: 15,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: xAxis,
      axisTick: {
        alignWithLabel: true,
      },
      axisLabel: {
        margin: 10,
        interval: 0,
        showMaxLabel: true,
        showMinLabel: true,
        color: '#000',
        opacity: 0.65,
        formatter(value: string) {
          const genreXlabelWidth = 1560;
          const _value = value?.split(' ');
          const len = _value.length;
          const lenFisrt = _value[0].length;
          if (len === 1) {
            if (value.length > 4) {
              return `${value.slice(0, lenFisrt)}\n
                ${value.slice(lenFisrt + 1)}`;
            }
            return value;
          }
          if (document.body.clientWidth >= 1910) {
            return `${_value[0]}\n${len === 2 ? _value[1] : `${_value[1]}...`}`;
          }
          if (document.body.clientWidth > genreXlabelWidth) {
            return lenFisrt > 7
              ? `${value.slice(0, 7)}\n${value.slice(8, 14)}${value.length > 14 ? '...' : ''
              }`
              : `${_value[0]}\n${len === 2 ? _value[1] : `${_value[1]}...`}`;
          }
          if (value.length > 4) {
            return `${value.slice(0, lenFisrt)}\n${value.slice(lenFisrt + 1)}`;
          }

          return value;
        },
      },
      axisLine: {
        show: true,
        onZero: false, // 当y轴有负数时,让x轴下落在y轴最小值
        lineStyle: {
          color: '#BFBFBF',
          // 透明度会展示到虚线，暂时用纯色代替
          // color: '#000',
          // opacity: 0.25,
          width: 1,
        },
      },
    },
    legend: {
      top: 'bottom',
      icon: 'circle',
      type: 'scroll',
      textStyle: {
        // 样式
        fontSize: 12,
        color: '#888',
      },
      pageIconSize: 10,
      pageIcons: {
        horizontal: [
          'path://M375.9 512.1c0 8.3 3.1 15.8 8.3 21.5l2.2 2.2 21.5 21.5L593.6 743c12.4 12.4 32.8 12.4 45.3 0 12.4-12.4 12.4-32.8 0-45.3L453.1 512l185.7-185.8c12.4-12.4 12.4-32.8 0-45.3-12.4-12.4-32.8-12.4-45.2 0.1L407.9 466.7l-21.8 21.8-1.8 1.8c-5.3 5.7-8.5 13.4-8.4 21.8z',
          'path://M639.7 490.3l-1.8-1.8-21.8-21.8L430.4 281c-12.4-12.5-32.8-12.5-45.2-0.1-12.4 12.5-12.4 32.9 0 45.3L570.9 512 385.1 697.7c-12.4 12.5-12.4 32.9 0 45.3 12.5 12.4 32.9 12.4 45.3 0l185.7-185.7 21.5-21.5 2.2-2.2c5.2-5.7 8.3-13.2 8.3-21.5 0.1-8.4-3.1-16.1-8.4-21.8z'
        ]
      },
      padding: [16, 0, 0, 0],
      pageTextStyle: {
        color: '#707070', // 翻页数字颜色
      }, // 翻页数字设置
      itemWidth: 8,
      itemHeight: 8,
      itemGap: 20,
      data: legend,
      selected: legendSelected,
      bottom: 0,
      selectedMode: true,
      formatter(name: string) {
        return name;
      },
      tooltip: {
        show: true,
        // backgroundColor: '#2e323a',
        transitionDuration: 0.2,
        textStyle: {
          color: 'white',
        },
        position: 'top',
        padding: 10,
        formatter: legendTooltipFormatter,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
      },
    },
    yAxis: [
      {
        type: 'value',
        alignTicks: true,//与右边的Y轴splitLine虚线对齐，右边要显示负辆
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed', // 设置分隔线为虚线
          },
        },
        min: 0,
        max: function (value: any) {
          let numStrTemp = parseInt(value.max + value.max * 0.5).toString();
          let newNumStr = Number(numStrTemp.slice(0, 3));
          let newNumStrEnd = [...numStrTemp.slice(1)];
          let newNum = Math.round(newNumStr * 0.01).toString();
          newNumStrEnd?.map(() => {
            newNum = newNum + '0';
          });
          return Number(newNum);
        },
        axisLabel: {
          formatter: (value: any) => `${mathFormat(value, true)}`,
        },
      },
      {
        type: 'value',
        splitLine: {
          show: false,
        },
        // min: 0,//不要设置min，要自动展示Y轴的负值
        splitNumber: splitNumber || 3,
        axisLabel: {
          formatter: (value: number) =>
            `${value === 0 ? value : toFixed0(value * 100, 0)}`,
        },
      },
    ],
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255, 255, 255, 0)',
      padding: 0,
      borderWidth: 0,
      axisPointer: {
        type: 'line',
      },
      formatter: (v:any)=>tooltipFormatter?.(v, locale, tooltipTitle),
    },
    series: recalcTotalSerie(series, legendSelected),
    ...resetProps,
  }
};
